import React, {
  Fragment,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import L, { LatLng } from "leaflet";
import { Toaster } from "ui-widgets";
import ModalControlProps from "models/explorer/modal-control-props";
import ExplorerService from "services/ExplorerService";
import BoundaryOrPoint from "models/explorer/boundary-or-point";

const { explorerService } = new ExplorerService();

const SystemBoundariesAndPoints = (modalControlProps: ModalControlProps) => {
  const {
    customSearch,
    handleCustomSearch,
    searchFeatures,
    handleSearchFeatures,
    setPointsAndBoundaries,
  } = modalControlProps;
  const [boundariesOrPointsOptions, setBoundariesOrPointsOptions] = useState<
    BoundaryOrPoint[]
  >([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController>();

  function selectSystemBoundaryOrPoint(
    _event: SyntheticEvent<Element, Event>,
    selectedValue: BoundaryOrPoint | null,
  ) {
    if (selectedValue?.guid) {
      const newCustomSearch = customSearch;
      newCustomSearch.enabled = true;
      handleSearchFeatures({
        ...searchFeatures,
        systemGuid: selectedValue.guid,
      });
      if (selectedValue.type === "boundary") {
        newCustomSearch.boundary_guid = selectedValue.guid;
        newCustomSearch.type = "system-boundary";
        const leafletGeojson = selectedValue.geojson
          ? L.GeoJSON.coordsToLatLngs(
              JSON.parse(selectedValue.geojson)?.coordinates,
              2,
            )
          : [];
        selectedValue.leafletGeojson = leafletGeojson;
      } else if (selectedValue.type === "point") {
        newCustomSearch.latlong = new LatLng(
          selectedValue.latitude,
          selectedValue.longitude,
        );
        newCustomSearch.type = "marker";
      }
      handleCustomSearch(newCustomSearch);
      setPointsAndBoundaries([selectedValue]);
    }
  }

  useEffect(() => {
    if (inputValue) {
      setLoading(true);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      const signal = controller.signal;
      abortRef.current = controller;
      explorerService
        .boundaryPointSearch(inputValue, 50, signal)
        .then((res) => {
          res
            ? setBoundariesOrPointsOptions(res)
            : setBoundariesOrPointsOptions([]);
        })
        .catch((err) => {
          if (err.name !== "AbortError") {
            // Check if the error is due to abort
            console.error(err);
            Toaster.makeToast(
              err?.message
                ? err.message
                : "An error occured when searching boundary or point",
              "error",
            );
          }
        })
        .finally(() => setLoading(false));
    } else {
      setBoundariesOrPointsOptions([]);
    }
  }, [inputValue]);

  return (
    <Autocomplete
      id="tags-explore"
      options={boundariesOrPointsOptions.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      })}
      renderInput={(params) => (
        <TextField
          {...params}
          label="System search"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </Fragment>
            ),
          }}
        />
      )}
      onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
      noOptionsText={
        !inputValue ? "Start typing to search" : "No options available"
      }
      fullWidth
      sx={{ my: 1 }}
      onChange={selectSystemBoundaryOrPoint}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.guid}>
            {option.name}
          </li>
        );
      }}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      getOptionLabel={(option) => option.name}
    />
  );
};

export default SystemBoundariesAndPoints;
